import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { AppShell } from '@/components/AppShell';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { showToast } from '@/components/ui/Toast';
import { PullToRefresh } from '@/components/PullToRefresh';
import { assignmentRepo } from '@/data/repositories/assignmentRepo';
import { syncService } from '@/services/sync';
import { gpsService } from '@/services/gps';
import { logQueueService } from '@/services/logQueue';
import { haptics } from '@/lib/haptics';
import { groupStops, aggregateBills, isDockStop, type Stop } from '@/lib/grouping';
import { DevSwitch, LogTypes } from '@/types/domain';

export function LoadPage() {
  const navigate = useNavigate();
  const [dock, setDock] = useState(false);
  const [batching, setBatching] = useState(false);
  const [autoSelected, setAutoSelected] = useState(false);

  useEffect(() => {
    syncService.setContext('loadUnload');
  }, []);

  const items = useLiveQuery(() => assignmentRepo.getLoadItems(), [], []);
  const stops = items ? groupStops(items, 'pickup', dock) : undefined;
  const dockBills = dock && stops ? stops.flatMap((s) => s.bills) : [];

  // Bill counts per tab, shown on the tab labels. Dock-routed pickups live
  // ONLY on the ON DOCK tab; without a count there, drivers reported
  // dispatched calls as "not on my phone" (they were, on the other tab).
  const shipperCount = items ? items.filter((a) => !isDockStop('pickup', a)).length : 0;
  const dockCount = items ? items.length - shipperCount : 0;

  // First load only: if the default tab is empty but the other has work,
  // start on the tab that has it. Never fight a manual tab choice after.
  useEffect(() => {
    if (autoSelected || !items || items.length === 0) return;
    setAutoSelected(true);
    if (shipperCount === 0 && dockCount > 0) setDock(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Native ON DOCK flow: one batch button loads EVERY dock bill (Loaded at
  // Terminal dock), moves them to the unload list (switch 2), then returns.
  async function loadAllAtDock() {
    if (dockBills.length === 0) return;
    setBatching(true);
    try {
      const fix = gpsService.peekFix();
      for (const b of dockBills) {
        await logQueueService.enqueue({
          logType: LogTypes.loadedAtTerminalDock,
          display: 'Display',
          assignmentId: b.remF04Id,
          tag: b.remF02Tag ?? undefined,
          latitude: fix?.latitude,
          longitude: fix?.longitude,
          accuracy: fix?.accuracy,
        });
        await assignmentRepo.setLocalSwitch(b.remF04Id, DevSwitch.Pickup);
      }
      haptics.medium();
      showToast(`${dockBills.length} item(s) loaded at dock.`, 'success');
      navigate('/main');
    } finally {
      setBatching(false);
    }
  }

  return (
    <AppShell title="Available pickups" showBack>
      <PullToRefresh onRefresh={() => syncService.runOnce('manual')}>
      <div className="mb-3 flex gap-2">
        <Button variant={!dock ? 'pickup' : 'ghost'} size="sm" onClick={() => setDock(false)}>
          SHIPPER{shipperCount > 0 ? ` (${shipperCount})` : ''}
        </Button>
        <Button variant={dock ? 'pickup' : 'ghost'} size="sm" onClick={() => setDock(true)}>
          ON DOCK{dockCount > 0 ? ` (${dockCount})` : ''}
        </Button>
      </div>

      {stops === undefined ? (
        <p className="text-brand-muted">Loading...</p>
      ) : stops.length === 0 ? (
        <Card>
          <CardBody>
            <p className="text-brand-muted">No pickups on this tab.</p>
            {(dock ? shipperCount : dockCount) > 0 && (
              <Button variant="pickup" onClick={() => setDock(!dock)}>
                {dock
                  ? `${shipperCount} pickup(s) on the SHIPPER tab`
                  : `${dockCount} pickup(s) on the ON DOCK tab`}
              </Button>
            )}
            <Button variant="primary" onClick={() => void syncService.runOnce('manual')}>
              Refresh
            </Button>
          </CardBody>
        </Card>
      ) : (
        <>
          <ul className="space-y-3">
            {stops.map((s) => (
              <StopCard
                key={s.key}
                stop={s}
                dock={dock}
                onOpen={() => navigate(`/load/${s.bills[0].remF04Id}`)}
              />
            ))}
          </ul>

          {dock && dockBills.length > 0 && (
            <div className="pb-2-safe sticky bottom-0 mt-4 bg-brand-bg pt-3">
              <Button
                variant="pickup"
                size="xl"
                fullWidth
                disabled={batching}
                onClick={() => void loadAllAtDock()}
              >
                {batching ? 'Loading...' : 'LOAD AT DOCK'}
              </Button>
            </div>
          )}
        </>
      )}
      </PullToRefresh>
    </AppShell>
  );
}

function StopCard({ stop, dock, onOpen }: { stop: Stop; dock: boolean; onOpen: () => void }) {
  const probe = stop.bills.some((b) => b.remProbRequirement && b.remProbRequirement.trim());
  const { pcs, wgt, refs } = aggregateBills(stop.bills);
  return (
    <li>
      <Card
        accent="pickup"
        className={dock ? undefined : 'cursor-pointer'}
        onClick={dock ? undefined : onOpen}
      >
        <CardHeader>
          <CardTitle>{stop.name || 'Pickup'}</CardTitle>
          <span className="text-sm font-bold text-brand-pickup">
            {stop.bills.length} bill{stop.bills.length === 1 ? '' : 's'}
          </span>
        </CardHeader>
        <CardBody>
          <div className="text-sm">
            <strong>From:</strong> {stop.address || '--'}
            {stop.city ? `, ${stop.city}` : ''}
          </div>
          <div className="mt-1 text-xs text-brand-muted">
            {stop.bills.length} bill{stop.bills.length === 1 ? '' : 's'} &middot; {pcs} pcs &middot;{' '}
            {wgt} lbs
          </div>
          {refs && (
            <div className="mt-1 truncate text-xs text-brand-muted">Ref: {refs}</div>
          )}
          <div className="mt-1 text-xs text-brand-muted">
            {stop.bills.map((b) => b.remF02Tag).filter(Boolean).join(', ')}
          </div>
          {probe && (
            <div className="mt-1 inline-block rounded bg-brand-warn px-2 py-0.5 text-xs font-bold text-brand-textDark">
              Temperature probe required
            </div>
          )}
        </CardBody>
      </Card>
    </li>
  );
}
